/**
 * Taking a table away as a CSV.
 *
 * Four questions and no format picker: CSV is the only exporter this fork has,
 * so a format control would offer one option. What is worth asking is *which
 * rows* -- a view exports what that view shows, filters, sorts and hidden
 * columns included, which is almost always what somebody means -- and the three
 * things that decide whether the file opens properly at the other end.
 *
 * The file arrives by plain link rather than through a fetch. The `user_session`
 * cookie is set on the registrable domain precisely so a `GET` for a file
 * authenticates without an `Authorization` header, which is what makes
 * `secure_file_serve` work at all; a download built here would have to
 * reimplement that and would gain nothing.
 */
import { useState } from 'react'
import { Download } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { CsvColumnSeparator, ExportCharset, View } from '@/api/types'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Label } from '@/shared/ui/label'
import { NativeSelect, NativeSelectOption } from '@/shared/ui/native-select'
import { Progress } from '@/shared/ui/progress'
import { hasExportFailed, isExportRunning, useExportJob, useStartExport } from './queries'

/**
 * The separators, with what each is for.
 *
 * The three control characters the endpoint accepts are not offered: they exist
 * for machine-to-machine use, and nothing that opens a CSV by hand will read
 * one. A comma and a semicolon are the two that matter -- a spreadsheet in a
 * locale that writes decimals with a comma expects the semicolon.
 */
const SEPARATORS = [',', ';', '|', 'tab'] as const satisfies readonly CsvColumnSeparator[]

/**
 * The character sets worth putting in a list somebody reads.
 *
 * The endpoint accepts thirty-three. Offering all of them would be a menu
 * nobody can choose from; these are UTF-8, the two Windows encodings a
 * spreadsheet is most likely to be opened with, and Latin-1.
 */
const CHARSETS: readonly ExportCharset[] = [
  'utf-8',
  'windows-1252',
  'windows-1250',
  'iso-8859-1',
]

export function ExportDialog({
  tableId,
  view,
  onClose,
}: {
  tableId: number
  /** The view on screen, if there is one: exporting it is the common case. */
  view: View | undefined
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [scope, setScope] = useState<'view' | 'table'>(
    view === undefined ? 'table' : 'view',
  )
  const [charset, setCharset] = useState<ExportCharset>('utf-8')
  const [separator, setSeparator] = useState<CsvColumnSeparator>(',')
  const [includeHeader, setIncludeHeader] = useState(true)
  const [jobId, setJobId] = useState<number | null>(null)

  const start = useStartExport(tableId)
  const job = useExportJob(jobId).data
  const running = jobId !== null && (job === undefined || isExportRunning(job))
  const failed = hasExportFailed(job)
  const ready = job?.state === 'finished' ? job : undefined

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('export.title')}</DialogTitle>
          <DialogDescription>{t('export.description')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {view !== undefined && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="export-scope">{t('export.rows')}</Label>
              <NativeSelect
                id="export-scope"
                value={scope}
                onChange={(event) => {
                  setScope(event.target.value === 'table' ? 'table' : 'view')
                }}
              >
                <NativeSelectOption value="view">
                  {t('export.thisView', { name: view.name })}
                </NativeSelectOption>
                <NativeSelectOption value="table">
                  {t('export.wholeTable')}
                </NativeSelectOption>
              </NativeSelect>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="export-separator">{t('export.separator')}</Label>
            <NativeSelect
              id="export-separator"
              value={separator}
              onChange={(event) => {
                setSeparator(event.target.value as CsvColumnSeparator)
              }}
            >
              {SEPARATORS.map((value) => (
                <NativeSelectOption key={value} value={value}>
                  {t(`export.separator_${value}`)}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="export-charset">{t('export.charset')}</Label>
            <NativeSelect
              id="export-charset"
              value={charset}
              onChange={(event) => {
                setCharset(event.target.value as ExportCharset)
              }}
            >
              {CHARSETS.map((value) => (
                <NativeSelectOption key={value} value={value}>
                  {value}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="export-header"
              checked={includeHeader}
              onCheckedChange={(checked) => {
                setIncludeHeader(checked === true)
              }}
            />
            <Label htmlFor="export-header">{t('export.header')}</Label>
          </div>

          {running && (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-muted-foreground">{t('export.working')}</p>
              <Progress value={job?.progress_percentage ?? 0} />
            </div>
          )}
          {failed && <p className="text-sm text-destructive">{t('export.failed')}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          {ready === undefined ? (
            <Button
              disabled={running}
              onClick={() => {
                setJobId(null)
                start.mutate(
                  {
                    viewId: scope === 'view' && view !== undefined ? view.id : null,
                    charset,
                    separator,
                    includeHeader,
                  },
                  {
                    onSuccess: (made) => {
                      setJobId(made.id)
                    },
                  },
                )
              }}
            >
              {t('export.start')}
            </Button>
          ) : (
            // An anchor rather than a button: the file is a place, and the
            // browser's own download is the thing that knows how to save it.
            <Button asChild>
              <a href={ready.url} download>
                <Download className="size-4" />
                {t('export.download')}
              </a>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
